/**
 * In-flight registries for party fetches.
 *
 * These deliberately live outside the store. redux-observable hands an action to the
 * epics *after* the reducers have processed it, so an epic can never dedupe itself
 * against a flag its own action has just written. The `pending` map in the party slice
 * mirrors these sets for the UI; these sets are what actually gate the network calls.
 */
const fullFetches = new Set<string>()
const bulkFetches = new Set<string>()

/**
 * A full fetch (GET /parties/:id) is only redundant against another full fetch. The bulk
 * payload omits accounts[], so an in-flight bulk request can never stand in for one.
 */
export const isFullFetchRedundant = (id: string): boolean => fullFetches.has(id)

/**
 * A bulk fetch (POST /parties/bulk) is redundant when the id is covered by either
 * registry — the full party payload is a superset of the bulk one.
 */
export const isBulkFetchRedundant = (id: string): boolean => fullFetches.has(id) || bulkFetches.has(id)

/**
 * Register a full fetch. Returns the release fn to call when the request settles.
 */
export const trackFullFetch = (id: string): (() => void) => {
  fullFetches.add(id)
  return () => {
    fullFetches.delete(id)
  }
}

/**
 * Register a bulk fetch. Returns the release fn to call when the request settles.
 */
export const trackBulkFetch = (ids: string[]): (() => void) => {
  ids.forEach(id => bulkFetches.add(id))
  return () => {
    ids.forEach(id => bulkFetches.delete(id))
  }
}

/**
 * Test-only. The registries are module state and so outlive an individual test store.
 */
export const resetInFlightFetches = (): void => {
  fullFetches.clear()
  bulkFetches.clear()
}
